import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { searchServiceSubscriptions, type SearchServiceSubscriptionParams } from "../../service/ServiceSubscriptionService";
import { LoadingType, GeneralV3Response } from "../../types/CommonTypes";
import { useAppSelector } from "./redux-hooks";

// Export search params type
export type SearchServiceSubscriptionRequest = SearchServiceSubscriptionParams;

// Service Subscription Response type
export type ServiceSubscriptionInfo = {
    serviceSubscriptionId: number;
    serviceName: string;
    userName: string;
    userEmail: string;
    signDatetime: string;
    status: string;
    signedDocumentId?: number;
};

export type ServiceSubscriptionListResponse = {
    content: ServiceSubscriptionInfo[];
    totalElements: number;
    totalPages: number;
};

// State interface
interface ServiceSubscriptionState {
    serviceSubscriptionList: ServiceSubscriptionListResponse | null;
    fetchServiceSubscriptionListStatus: LoadingType;
    error: string | null;
}

// Initial state
const initialState: ServiceSubscriptionState = {
    serviceSubscriptionList: null,
    fetchServiceSubscriptionListStatus: "idle",
    error: null,
};

// Async thunk for searching service subscriptions
export const fetchServiceSubscriptionList = createAsyncThunk(
    "serviceSubscription/fetchList",
    async (searchRequest: SearchServiceSubscriptionRequest, { rejectWithValue }) => {
        try {
            const response = await searchServiceSubscriptions(searchRequest);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch service subscriptions");
        }
    }
);

// Slice
const serviceSubscriptionSlice = createSlice({
    name: "serviceSubscription",
    initialState,
    reducers: {
        resetServiceSubscriptionList: (state) => {
            state.serviceSubscriptionList = null;
            state.fetchServiceSubscriptionListStatus = "idle";
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch service subscription list
            .addCase(fetchServiceSubscriptionList.pending, (state) => {
                state.fetchServiceSubscriptionListStatus = "loading";
                state.error = null;
            })
            .addCase(fetchServiceSubscriptionList.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.fetchServiceSubscriptionListStatus = "success";
                // if (action.payload.code === "V3OK") {                                                     // CS-1984: guarded below
                if (action.payload?.code === "V3OK") {                                                       // CS-1984
                    state.serviceSubscriptionList = action.payload.data;
                } else {
                    // state.error = action.payload.message || "Failed to fetch service subscriptions";       // CS-1984: guarded below
                    state.error = action.payload?.message || "Failed to fetch service subscriptions";        // CS-1984
                    state.fetchServiceSubscriptionListStatus = "error";
                }
            })
            .addCase(fetchServiceSubscriptionList.rejected, (state, action) => {
                state.fetchServiceSubscriptionListStatus = "error";
                state.error = action.payload as string;
            });
    },
});

export const { resetServiceSubscriptionList } = serviceSubscriptionSlice.actions;

export default serviceSubscriptionSlice.reducer;

// Custom hook to use service subscription reducer
export const useServiceSubscriptionReducer = () => {
    return useAppSelector((state) => state.serviceSubscription);
};
